import React, { useState } from "react";
import { toast } from "sonner";
import Seo from "../components/site/Seo";
import Reveal from "../components/site/Reveal";
import { SectionLabel, PlaceholderNote, PrimaryButton, ArrowLink } from "../components/site/Bits";
import { Field, TextAreaField, SelectField, FormSuccess } from "../components/site/Fields";
import ClosingCTA from "../components/home/ClosingCTA";
import { SEO, COLLABORATION, PARTNERSHIP_TYPES, COMPANY } from "../lib/content";
import { IMAGES } from "../lib/images";
import { postPartnerRequest, extractError } from "../lib/api";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[a-zA-Z]{2,}$/;

const emptyForm = {
  organisation: "",
  contact_name: "",
  email: "",
  phone: "",
  partnership_type: "",
  message: "",
};

export default function PartnersPage() {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (form.organisation.trim().length < 2) next.organisation = "Please enter your organisation name.";
    if (form.contact_name.trim().length < 2) next.contact_name = "Please enter a contact name.";
    if (!EMAIL_RE.test(form.email.trim())) next.email = "Please enter a valid email address.";
    if (!form.partnership_type) next.partnership_type = "Please choose a partnership type.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await postPartnerRequest(form);
      setSent(true);
      setForm(emptyForm);
      toast.success("Partnership request sent to LYKIT.");
    } catch (error) {
      toast.error(extractError(error));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Seo title={SEO.partners.title} description={SEO.partners.description} path="/partners" />

      {/* ---------------------------------------------- LYKIT x IBMCT header */}
      <section
        className="relative isolate overflow-hidden bg-nearblack"
        data-testid="collaboration-hero"
        data-no-reveal
      >
        <div className="lk-container-wide relative z-20 pb-[clamp(40px,4.4vw,66px)] pt-[clamp(124px,14vh,168px)]">
          <div className="mx-auto max-w-[900px] text-center">
            <Reveal>
              <span className="eyebrow eyebrow-on-dark">{COLLABORATION.eyebrow}</span>
            </Reveal>
            <Reveal delay={0.08}>
              <h1
                className="display-xl mt-5"
                style={{ color: "#FBFAF7" }}
                data-testid="collaboration-title"
              >
                LYKIT ENTERTAINMENT{" "}
                <span
                  className="editorial-sm"
                  style={{ color: "#E7D6B2", fontSize: "0.82em", lineHeight: 1 }}
                  aria-hidden="true"
                >
                  &#215;
                </span>{" "}
                IBMCT
              </h1>
            </Reveal>
            <Reveal delay={0.16}>
              <p
                className="body-large mx-auto mt-6 max-w-[62ch]"
                style={{ color: "rgba(247,246,242,0.78)" }}
                data-testid="collaboration-lead"
              >
                {COLLABORATION.lead}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ------------------------------------- the single collaboration block */}
      <section className="lk-section" data-testid="collaboration-section">
        <div className="lk-container-wide">
          <div className="mx-auto max-w-[1020px]">
            <Reveal variant="image">
              <figure
                className="img-frame relative w-full"
                style={{ aspectRatio: "3 / 2", border: "1px solid rgba(23,21,34,0.12)" }}
              >
                <img
                  src={IMAGES.collaboration.ibmct}
                  alt="LYKIT Entertainment founders with IBMCT during the crowdfunding collaboration announcement"
                  data-testid="collaboration-image"
                  loading="lazy"
                />
              </figure>
            </Reveal>

            <div className="mx-auto mt-[clamp(30px,3.2vw,52px)] max-w-[720px] text-center">
              <Reveal>
                <span className="gold-rule mx-auto" style={{ width: 40 }} />
                <p className="editorial-sm mt-7" data-testid="collaboration-name">
                  {COLLABORATION.shortTitle}
                </p>
              </Reveal>

              <Reveal delay={0.08}>
                <div className="mt-6 space-y-5">
                  {COLLABORATION.body.map((paragraph) => (
                    <p key={paragraph} className="body-muted text-[15px] leading-[1.65]">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </Reveal>

              <Reveal delay={0.14}>
                <div className="mt-9 flex justify-center">
                  <ArrowLink
                    href={COLLABORATION.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="collaboration-ibmct-link"
                  >
                    {COLLABORATION.linkLabel}
                  </ArrowLink>
                </div>
              </Reveal>
            </div>

            {/* three quiet supporting points */}
            <div className="mt-[clamp(36px,3.6vw,60px)] grid gap-x-12 sm:grid-cols-3">
              {COLLABORATION.points.map((point, i) => (
                <Reveal
                  key={point.title}
                  delay={i * 0.06}
                  className="py-7 sm:px-7 sm:first:pl-0"
                  style={{ borderTop: "1px solid rgba(23,21,34,0.10)" }}
                >
                  <span className="project-meta" style={{ color: "rgba(23,21,34,0.38)" }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="display-sm mt-3">{point.title}</h3>
                  <p className="body-muted mt-2 max-w-[32ch] text-[14px] leading-[1.62]">{point.text}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------- become a partner */}
      <section
        style={{ background: "var(--lykit-lavender)" }}
        data-testid="become-partner-section"
        id="become-a-partner"
      >
        <div className="lk-container-wide py-[clamp(40px,4.2vw,72px)]">
          <div className="grid gap-y-9 lg:grid-cols-12 lg:gap-x-16">
            <div className="lg:col-span-5">
              <Reveal>
                <SectionLabel>Become a Partner</SectionLabel>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="display-lg mt-5 max-w-[16ch]">LET'S BUILD SOMETHING TOGETHER.</h2>
                <p className="lead measure mt-6">
                  Tell us about your organisation and what you would like to build with LYKIT. Our partnerships team
                  reviews every request.
                </p>
                <a
                  href={`mailto:${COMPANY.email}`}
                  data-testid="partners-email-link"
                  className="mt-7 inline-block text-[14px]"
                  style={{ color: "#352A59" }}
                >
                  {COMPANY.email}
                </a>
              </Reveal>
            </div>

            <div className="lg:col-span-6 lg:col-start-7">
              {sent ? (
                <FormSuccess
                  testId="partner-form-success"
                  title="REQUEST RECEIVED."
                  message="Thank you. Our partnerships team will review your request and get in touch with you."
                >
                  <PrimaryButton variant="outline" onClick={() => setSent(false)} data-testid="partner-form-reset">
                    Send Another Request
                  </PrimaryButton>
                </FormSuccess>
              ) : (
                <form onSubmit={onSubmit} data-testid="partner-request-form" noValidate>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field
                      label="Organisation"
                      name="organisation"
                      required
                      value={form.organisation}
                      onChange={onChange}
                      error={errors.organisation}
                      placeholder="Company or organisation"
                    />
                    <Field
                      label="Contact Name"
                      name="contact_name"
                      required
                      value={form.contact_name}
                      onChange={onChange}
                      error={errors.contact_name}
                      placeholder="Your name"
                    />
                    <Field
                      label="Email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={onChange}
                      error={errors.email}
                      placeholder="you@company.com"
                    />
                    <Field
                      label="Phone"
                      name="phone"
                      value={form.phone}
                      onChange={onChange}
                      error={errors.phone}
                      placeholder="Optional"
                    />
                    <div className="sm:col-span-2">
                      <SelectField
                        label="Partnership Type"
                        name="partnership_type"
                        required
                        value={form.partnership_type}
                        onChange={onChange}
                        options={PARTNERSHIP_TYPES}
                        error={errors.partnership_type}
                        placeholder="Select partnership type"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <TextAreaField
                        label="Message"
                        name="message"
                        value={form.message}
                        onChange={onChange}
                        placeholder="What would you like to build with LYKIT?"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="partner-submit-button"
                    className="btn btn-primary mt-8"
                    style={sending ? { opacity: 0.7, cursor: "wait" } : undefined}
                  >
                    {sending ? "Sending…" : "Become a Partner"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <ClosingCTA />
    </>
  );
}
